import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { TriggerRule, Question, TriggerCondition } from "@/types/theme";
import { toast } from "@/hooks/use-toast";
import { TriggerRuleItem } from "./TriggerRuleItem";
import { useBuilder } from "@/contexts/BuilderContext";

interface TriggerRulesTabProps {
  chatbotId: string;
  triggerRules: TriggerRule[];
  questions: Question[];
  onAddTriggerRule: () => void;
  onUpdateTriggerRule: (ruleId: string, updatedRule: Partial<TriggerRule>) => void;
  onDeleteTriggerRule: (ruleId: string) => void;
  reportUnsavedChanges: (isDirty: boolean) => void;
}

export const TriggerRulesTab = ({
  chatbotId,
  triggerRules,
  questions,
  onAddTriggerRule,
  onUpdateTriggerRule,
  onDeleteTriggerRule,
  reportUnsavedChanges,
}: TriggerRulesTabProps) => {
  // Use direct props instead of local state - no more conflicts!
  const [openRules, setOpenRules] = useState<Record<string, boolean>>({});
  const [localConditionValues, setLocalConditionValues] = useState<Record<string, string>>({});
  const [localRuleNames, setLocalRuleNames] = useState<Record<string, string>>({});

  // Get Data Collection fields from unified context at component level
  const { getSettings } = useBuilder();
  const settings = getSettings();
  const aiConfig = settings?.ai_chat_config;

  // Initialize local UI state from trigger rules (no conflicts since we don't reset)
  useEffect(() => {
    if (triggerRules) {
      const newConditionValues: Record<string, string> = {};
      const newRuleNames: Record<string, string> = {};

      triggerRules.forEach(rule => {
        // Only set if not already set (preserve user input)
        if (!localRuleNames[rule.id]) {
          newRuleNames[rule.id] = rule.name || '';
        }

        if (rule.conditions) {
          rule.conditions.forEach((condition, index) => {
            const key = `${rule.id}-${index}`;
            // Only set if not already set (preserve user input)
            if (!localConditionValues[key]) {
              newConditionValues[key] = typeof condition.value === 'string'
                ? condition.value
                : JSON.stringify(condition.value);
            }
          });
        }
      });

      setLocalConditionValues(prev => ({ ...newConditionValues, ...prev }));
      setLocalRuleNames(prev => ({ ...newRuleNames, ...prev }));
    }
  }, [triggerRules]);

  const toggleRule = (ruleId: string) => {
    setOpenRules(prev => ({
      ...prev,
      [ruleId]: !prev[ruleId]
    }));
  };

  const handleAddCondition = (ruleId: string) => {
    // Check if we have questions OR data collection fields
    const hasQuestions = questions.length > 0;
    const hasDataFields = aiConfig?.collectUserInfo &&
      (aiConfig.requiredFields?.length > 0 || aiConfig.optionalFields?.length > 0);

    if (!hasQuestions && !hasDataFields) {
      toast({
        title: "No trigger options available",
        description: "You need to add questions in the Flow tab or enable Data Collection in AI settings.",
        variant: "destructive",
      });
      return;
    }

    const ruleIndex = triggerRules.findIndex(r => r.id === ruleId);
    if (ruleIndex === -1) return;

    const ruleToUpdate = { ...triggerRules[ruleIndex] };

    // Determine the default field/question to use
    let defaultFieldId = '';
    if (hasQuestions) {
      defaultFieldId = questions[0].id;
    } else if (hasDataFields && aiConfig?.requiredFields?.length > 0) {
      const firstField = aiConfig.requiredFields[0];
      const fieldName = typeof firstField === 'string' ? firstField : firstField.name;
      defaultFieldId = `data_field:${fieldName}`;
    } else if (hasDataFields && aiConfig?.optionalFields?.length > 0) {
      const firstField = aiConfig.optionalFields[0];
      const fieldName = typeof firstField === 'string' ? firstField : firstField.name;
      defaultFieldId = `data_field:${fieldName}`;
    }

    const newCondition: TriggerCondition = {
      questionId: defaultFieldId,
      operator: 'equals',
      value: ''
    };

    ruleToUpdate.conditions = [...(ruleToUpdate.conditions || []), newCondition];

    // Use unified context instead of local state
    onUpdateTriggerRule(ruleId, { conditions: ruleToUpdate.conditions });
    reportUnsavedChanges(true);
  };

  const handleConditionValueChange = (ruleId: string, index: number, value: string) => {
    const key = `${ruleId}-${index}`;
    setLocalConditionValues(prev => ({
      ...prev,
      [key]: value
    }));
    reportUnsavedChanges(true);
  };

  const handleUpdateCondition = (rule: TriggerRule, index: number, updatedCondition: Partial<TriggerCondition>) => {
    const ruleIndex = triggerRules.findIndex(r => r.id === rule.id);
    if (ruleIndex === -1 || !triggerRules[ruleIndex].conditions) return;

    if (updatedCondition.hasOwnProperty('value')) {
      const key = `${rule.id}-${index}`;
      setLocalConditionValues(prev => ({
        ...prev,
        [key]: String(updatedCondition.value)
      }));
    }

    const updatedConditions = [...triggerRules[ruleIndex].conditions!];
    updatedConditions[index] = {
      ...updatedConditions[index],
      ...updatedCondition
    } as TriggerCondition;

    // Use unified context instead of local state
    onUpdateTriggerRule(rule.id, { conditions: updatedConditions });
    reportUnsavedChanges(true);
  };

  const handleDeleteCondition = (rule: TriggerRule, index: number) => {
    const ruleIndex = triggerRules.findIndex(r => r.id === rule.id);
    if (ruleIndex === -1 || !triggerRules[ruleIndex].conditions) return;

    const updatedConditions = [...triggerRules[ruleIndex].conditions!];
    updatedConditions.splice(index, 1);

    // Use unified context instead of local state
    onUpdateTriggerRule(rule.id, { conditions: updatedConditions });
    reportUnsavedChanges(true);
  };

  const handleSaveRule = (ruleId: string, updatedRuleData: Partial<TriggerRule>) => {
    // Use unified context instead of local state
    onUpdateTriggerRule(ruleId, updatedRuleData);
    reportUnsavedChanges(true);

    if (updatedRuleData.name) {
      setLocalRuleNames(prev => ({ ...prev, [ruleId]: updatedRuleData.name as string }));
    }

    toast({
      title: "Rule update initiated",
      description: "Your changes have been sent for saving.",
    });
  };

  const handleDeleteRuleWrapper = (ruleId: string) => {
    onDeleteTriggerRule(ruleId);
    reportUnsavedChanges(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-800">Trigger Rules</h4>
        {triggerRules && triggerRules.length > 0 && (
          <Button onClick={onAddTriggerRule}>
            <Plus className="mr-2 h-4 w-4" />
            Add Rule
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {triggerRules && triggerRules.map(rule => (
          <TriggerRuleItem
            key={rule.id}
            rule={rule}
            isOpen={openRules[rule.id] || false}
            localRuleName={localRuleNames[rule.id] || ''}
            questions={questions || []}
            onToggle={() => toggleRule(rule.id)}
            onDelete={() => handleDeleteRuleWrapper(rule.id)}
            onAddCondition={() => handleAddCondition(rule.id)}
            localConditionValues={localConditionValues}
            onSaveRule={handleSaveRule}
            onUpdateCondition={(index, updatedCondition) => handleUpdateCondition(rule, index, updatedCondition)}
            onDeleteCondition={(index) => handleDeleteCondition(rule, index)}
            reportUnsavedChanges={reportUnsavedChanges}
          />
        ))}

        {(!triggerRules || triggerRules.length === 0) && (
          <div className="text-center py-10 border rounded-lg bg-gray-50">
            <p className="text-gray-500 mb-4">No trigger rules added yet</p>
            <Button onClick={onAddTriggerRule}>
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Rule
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
